/**
 * The wire shape of `/grants/*` — scoped role grants and the invitations that create them (#261).
 *
 * Mirrors `API.Core/Models/Authentication/RoleGrantModels.cs` +
 * `API.Core/Models/Authentication/RoleGrant.cs` (`GrantScope`).
 *
 * **`GrantScope` is a NUMBER on the wire, not a string.** The monolith serialises with Newtonsoft
 * (`API/Program.cs` → `AddNewtonsoftJson`) and registers no `StringEnumConverter`, so a C# enum crosses as its
 * ordinal — exactly as documented for the context enums in `_auth/context/context.models.ts`. A string enum
 * would compile, render "undefined" and POST a `Scope` the backend reads as `Company`, silently issuing a
 * company-wide invitation where a shard one was meant. The ordinals here are load-bearing and must stay in the
 * C# declaration order.
 *
 * `NullValueHandling.Ignore` is also set, so every nullable field (`shardId`, `label`, `boundEmail`,
 * `redeemedAt`, `revokedAt`, `code`, …) is **omitted** rather than sent as null — hence the `?` on each.
 */

import {roleDescription, roleLabel} from '../../../_auth/context/context.models';
// One reader for "where does the code live on a failed response", reused rather than re-implemented — two
// copies of that question is how one of them ends up reading the wrong field.
import {readErrorCode} from '../../../_auth/two-factor/second-factor-errors';

/**
 * Re-exported so this module has one role vocabulary rather than a second copy of the same switch —
 * {@link roleDescription} included, because the sentence an administrator reads before granting a role and the
 * sentence an invitee reads before accepting it must be the same sentence.
 */
export {roleDescription, roleLabel};

/** How wide a grant reaches. Ordinals mirror `GrantScope` in `API.Core/Models/Authentication/RoleGrant.cs`. */
export enum GrantScope {
  /** A whole company, every shard it has. */
  Company = 0,
  /** One shard of a company. */
  Shard = 1
}

/**
 * An invitation as the management screens see it.
 *
 * `code` is populated **only** on the response to the call that created the invitation. The backend stores an
 * unsalted SHA-256 of it and nothing else, so it can never be read back or re-listed — see
 * {@link invitationCodeIsGone}.
 */
export interface RoleGrantInvitation {
  id: string;
  scope: GrantScope;
  companyId: string;
  shardId?: string;
  companyName?: string;
  shardName?: string;
  roles: string[];
  label?: string;
  boundEmail?: string;
  /**
   * Whether {@link boundEmail} is a **hard** requirement (#568): only an account whose provider-*verified*
   * address equals it may redeem. Mirrors `RoleGrantInvitationDto.EnforceBoundEmail`.
   *
   * Unlike {@link emailDelivery} this is a property of the invitation and is **stored**, so it is answered on
   * every read — the management list, and the anonymous landing preview, which is the only place it can be said
   * to the person it constrains.
   *
   * Optional rather than required because a `false` from an older backend during a rollout window would be
   * absent altogether. Every reader here treats `undefined` as "not enforced", which is the safe reading: an
   * absent flag must never make the UI promise a narrowing the server is not applying.
   */
  enforceBoundEmail?: boolean;
  /** ISO timestamp. */
  expiration: string;
  /** ISO timestamp. */
  created: string;
  redeemedAt?: string;
  redeemedByName?: string;
  revokedAt?: string;
  /** The one-time code — present exactly once, in the issue response. */
  code?: string;

  /**
   * Set when this is an **employee-link** invitation: redeeming it binds the redeeming account to that
   * employee and confers no role at all (`roles` is empty). Absent for an ordinary role-grant invitation.
   *
   * It is what tells the two flavours apart wherever they are rendered together — an invitation with no roles
   * and no employee would otherwise read as "grants nothing", which is true of neither.
   */
  employeeId?: string;
  /** That employee's display name, resolved server-side from their per-company row. */
  employeeName?: string;

  /**
   * What became of the invitation mail, for an issue call that asked for one. Present only on the response that
   * created the invitation — it describes the act of issuing, not the invitation, and is not stored.
   *
   * A NUMBER on the wire, like {@link GrantScope} — see the file header.
   */
  emailDelivery?: InviteEmailDelivery;

  /**
   * Which form the invite landing must render for a **native** (e-mail + password) redemption (#580). Mirrors
   * `RoleGrantInvitationDto.NativeRedeem`.
   *
   * **A string union, not an enum — and that is the opposite of every other wire enum in this file.** The
   * reason is the same trap, answered the other way round: the monolith serialises with Newtonsoft and
   * registers no `StringEnumConverter`, so a C# enum crosses as its ORDINAL, which is why {@link GrantScope}
   * and {@link InviteEmailDelivery} are numeric mirrors that must stay in the C# declaration order. The server
   * side of this field is therefore not an enum at all but three string constants
   * (`API.Core/Authorization/InvitationEmailGate.cs`, `NativeRedeemModes`), and a self-describing literal
   * cannot be silently re-mapped by a member being inserted above it.
   *
   * Optional because an older backend during a rollout window omits it altogether. Absent means "we were not
   * told", which no reader may read as a narrowing — the gate on the server decides, and this value only
   * spares somebody a refusal they had no way of predicting.
   */
  nativeRedeem?: NativeRedeemMode;

  /**
   * Where this invitation was mailed, when it was mailed at all (#584). Absent when the issuer handed the code
   * over. Mirrors `RoleGrantInvitationDto.DeliveryEmail`, and it is **stored**, so it is answered on every read.
   *
   * **Separate from {@link boundEmail} because the two decisions are separate.** An invitation may be sent to an
   * address without being redeemable only by it, which is exactly what the single on-screen toggle switched off
   * produces. Before #584 that state could not be expressed at all, so the list had to describe every delivery as
   * a binding.
   */
  deliveryEmail?: string;

  /**
   * ISO timestamp: when this invitation was mailed to {@link boundEmail} (#580). Absent when it was handed over
   * rather than mailed. Stored, so it is answered on every read.
   *
   * **What it is for on a management screen.** "Sent on Tuesday and still not accepted" is a different
   * situation from "the code was written on a note and nobody has typed it", and the two call for different
   * moves: the first is worth re-sending, the second is worth asking about in person. Without this field both
   * look identical in the list, because an unredeemed invitation says nothing about how it travelled.
   */
  deliveredByEmailAt?: string;
}

/**
 * Which form a **native** (e-mail + password) redemption of an invitation must take (#580).
 *
 * A union of the three literals `API.Core/Authorization/InvitationEmailGate.cs`'s `NativeRedeemModes` declares,
 * for the reason argued on {@link RoleGrantInvitation.nativeRedeem}: this one value crosses the wire as a
 * string rather than as a Newtonsoft ordinal, so the mirror is a union rather than a numbered enum.
 *
 *  * `open` — any address may be typed. The code is the whole secret, as it was before #568.
 *  * `matchRequired` — the typed address must equal the bound one. The invitation was mailed there, so the
 *    delivery already proves whoever holds the code holds the mailbox.
 *  * `confirmationRequired` — the typed address must equal the bound one AND a one-time code mailed to it must
 *    be presented, because the enforced invitation was handed over rather than mailed.
 */
export type NativeRedeemMode = 'open' | 'matchRequired' | 'confirmationRequired';

/**
 * What became of an invitation mail. Ordinals mirror `InviteEmailDelivery` in
 * `API.Core/Models/Authentication/RoleGrantModels.cs` and are load-bearing (Newtonsoft sends the ordinal).
 *
 * The code comes back **regardless** of what happened here, which is the point: a mail that could not be sent
 * must not also lose the invitation. A screen that asked for delivery and reads anything but {@link Sent} shows
 * the code after all, rather than claiming a mail is on its way.
 *
 * **Renamed from `EmployeeInviteEmailDelivery` in #568**, when the management invitation
 * (`POST /grants/invitations`) gained the same delivery and started answering with the same values — the old
 * name would have been actively wrong there, since nothing about it concerns an employee. The rename costs
 * nothing on the wire (the members and their ordinals are untouched, and the ordinal is all that crosses) and
 * no alias is kept, matching the C#: a second name for one enum is how two halves of a contract drift.
 */
export enum InviteEmailDelivery {
  /** No mail was asked for; the issuer hands the code over themselves. */
  NotRequested = 0,
  /** The mail went out to the bound address. */
  Sent = 1,
  /** A mail was asked for, but there is no usable address — the ordinary case on the employee path. */
  NoAddress = 2,
  /** A mail was asked for and the send failed. The invitation exists; hand the code over. */
  Failed = 3
}

/**
 * How an invitation is to reach the person it is for — the one vocabulary behind
 * `InviteDeliveryChoiceComponent`, shared by all three screens that issue one (#568).
 *
 * It lives here rather than on any one of those screens because it is half of a wire decision: `email` is
 * exactly `DeliverByEmail: true`, `link` is exactly `DeliverByEmail: false`, and `none` means no invitation
 * request is made at all. Keeping it beside {@link EmployeeLinkInviteRequest} and {@link RoleGrantInviteRequest}
 * is what stops a fourth screen inventing a fourth spelling of the same three outcomes.
 *
 * `none` is a first-class choice, not an absence, and only the roster's create form offers it: plenty of
 * employee records exist for people who will never sign in (a name on the shift plan, a historical row), and
 * forcing an invitation on every create would mint one-time codes nobody ever redeems.
 *
 * **Renamed `code` → `link` in #568**, together with the label it carries. What the dialog hands over is an
 * invitation *link*; the bare code is only useful to somebody who already knows where to paste it. A value
 * named after the lesser half of what the control produces is how a screen ends up describing itself wrongly.
 */
export type InviteDelivery = 'none' | 'link' | 'email';

/**
 * Whether a string is syntactically an address the server would accept as a binding.
 *
 * **A gate on what the form OFFERS, never a second authority on what is valid.** The server decides
 * (`RoleGrantService.IsUsableAddress` → `IsEmailAddress`) and answers `E_GRANT_INVITE_EMAIL_REQUIRED` when it
 * disagrees; this only keeps „Per E-Mail senden" and the binding toggle from being selectable while there is
 * nothing to select them for — an option that says "send this by e-mail" over an empty field is a promise the
 * request cannot keep.
 *
 * Deliberately loose (`something@something.tld`, no whitespace) rather than a full RFC 5322 attempt: a stricter
 * pattern here would *disable* a control for an address the server would have accepted, which is the failure
 * mode that cannot be worked around from the screen.
 */
export function looksLikeEmail(value: string | null | undefined): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value ?? '').trim());
}

/**
 * The body `POST /auth/account/admin/employees/{employeeId}/invitations` takes. Every field has a safe default
 * on the server, so an empty object issues a correctly bound invitation.
 */
export interface EmployeeLinkInviteRequest {
  /**
   * Whether to bind the invitation to the employee's stored address, so only an account reporting that address
   * may redeem it. The server reads the address itself — this only says whether to use it — and degrades
   * silently to unbound when the employee has none, which is the ordinary case.
   */
  BindToEmployeeEmail?: boolean;

  /**
   * Whether the server should mail the invitation to the employee's stored address instead of the issuer
   * handing the code over. Defaults to false server-side.
   *
   * Asking for it never costs the code: the response carries it either way, and {@link
   * RoleGrantInvitation.emailDelivery} says what actually happened — so a caller that asked for a mail and got
   * `NoAddress`/`Failed` must fall back to showing the code, not report success.
   */
  DeliverByEmail?: boolean;

  /**
   * Whether the resolved binding is a **hard** requirement — only a provider-verified address equal to the
   * employee's own may redeem (#568). Defaults to false server-side.
   *
   * Because the address is read from the employee row, it can only be honoured when one was actually bound:
   * asking for it with {@link BindToEmployeeEmail} off, or for an employee with no usable address, answers
   * `E_GRANT_INVITE_EMAIL_REQUIRED` rather than issuing an invitation whose enforcement enforces nothing. The
   * screens therefore offer it only alongside a bound address.
   */
  EnforceBoundEmail?: boolean;
  /** Note for the issuer's own reference; never shown to the invitee. */
  Label?: string | null;
}

/**
 * One active or revoked grant. Singular `role` on purpose: a grant confers exactly one role, so revoking it
 * must not disturb the others the same invitation created.
 */
export interface RoleGrant {
  id: string;
  externalIdentityId: string;
  identityName?: string;
  scope: GrantScope;
  companyId: string;
  shardId?: string;
  /**
   * The shard's display name, when the backend resolves one. Optional and **not** relied on: the roster has
   * always rendered the bare word „Standort" without it, and {@link grantScopeName} degrades to the id rather
   * than rendering a blank if it is absent.
   */
  shardName?: string;
  role: string;
  created: string;
  revokedAt?: string;

  /**
   * ### The #759 coverage contract — READ IT THROUGH {@link grantCoverage}, NEVER DIRECTLY
   *
   * A grant is *covered* when dropping it would change no token in any context its holder can select —
   * a shard-scoped `ShardAdmin` next to a company-scoped `CompanyOwner`, say. The backend derives that from
   * `RoleGrantExpander.Expand` itself (`GrantCoverage.Analyse`, `API.Core/Authorization/GrantCoverage.cs`),
   * so the rule cannot drift from the privilege computation it describes; nothing in the web app re-decides
   * it. Always `false` on a revoked row — "this access is redundant" says nothing about access that ended.
   *
   * These three fields are the **only** place the wire names appear. Every consumer goes through
   * {@link grantCoverage}, so rebinding them is a change to that one function and nowhere else.
   *
   * Mirrors `RoleGrantDto.Covered` / `.CoveredByGrantIds` / `.CoversGrantIds`.
   */
  covered?: boolean;

  /**
   * The ids of the active grants that make this one redundant — non-empty whenever {@link covered} is set,
   * and never naming another covered row. `ListGrants` always answers an array (empty when there is nothing
   * to say); the single-row revoke answers omit it, which means the same as empty.
   */
  coveredByGrantIds?: string[];

  /**
   * The inverse: the covered grants **this** one helps supersede — the "revoke these together" set.
   *
   * Answered from the same server-side computation rather than re-derived here by scanning every other row's
   * {@link coveredByGrantIds}, so the roster's badges and the revoke dialog's survivor list cannot disagree
   * about who covers whom.
   */
  coversGrantIds?: string[];
}

/**
 * Per-row coverage (#759), in the shape the screens actually consume: a boolean plus two resolved id lists
 * that are never `undefined`, so no caller has to defend against a backend that omits an empty array.
 */
export interface GrantCoverage {
  /** Whether this grant confers nothing its holder does not already hold through another active grant. */
  readonly covered: boolean;
  /** Which active grants make it redundant. Empty whenever {@link covered} is false. */
  readonly coveredByGrantIds: readonly string[];
  /** Which covered grants this one supersedes — what „Alle N entziehen" would take with it. */
  readonly coversGrantIds: readonly string[];
}

/**
 * **The single binding point for the #759 coverage contract.**
 *
 * Reads {@link RoleGrant.covered} / {@link RoleGrant.coveredByGrantIds} / {@link RoleGrant.coversGrantIds}
 * and normalises them. An older build's response — or any environment whose backend predates #759 — carries
 * none of them, and every grant then reports as uncovered and covering nothing, which is exactly the
 * pre-#759 behaviour: no badge, no second dialog action.
 *
 * `covered` is taken as the answer rather than inferred from a non-empty id list, so a backend that reports
 * coverage it cannot attribute to specific grants still lights the badge instead of silently dropping it.
 */
export function grantCoverage(grant: RoleGrant): GrantCoverage {
  return {
    covered: grant.covered === true,
    coveredByGrantIds: grant.coveredByGrantIds ?? [],
    coversGrantIds: grant.coversGrantIds ?? []
  };
}

/** Whether a grant is still in force — revoked rows neither cover anything nor are reported as covered. */
export function grantIsActive(grant: RoleGrant): boolean {
  return !grant.revokedAt;
}

/**
 * The active grants that make `grant` redundant, resolved from {@link GrantCoverage.coveredByGrantIds}
 * against the roster. An id the roster does not hold (a grant outside the loaded page) is skipped rather than
 * rendered as a placeholder.
 */
export function coveringGrants(grant: RoleGrant, all: readonly RoleGrant[]): RoleGrant[] {
  const ids = new Set(grantCoverage(grant).coveredByGrantIds);
  if (!ids.size) return [];
  return all.filter(other => other.id !== grant.id && grantIsActive(other) && ids.has(other.id));
}

/**
 * The active grants that `grant` covers — the ones that would survive a revoke of `grant` alone, and the ones
 * „Alle N entziehen" adds to it.
 *
 * Resolved from the server's own {@link RoleGrant.coversGrantIds} against the loaded roster, so the survivor
 * list and the `[abgedeckt]` badges are two readings of one computation. An id the roster does not hold is
 * skipped rather than named as a placeholder — the dialog's promise is that every line is a real row.
 *
 * ### The mutual case is broken here, deliberately
 *
 * Two identical grants cover each other. The backend settles that by flagging exactly one survivor, but a
 * partial page, a stale list or a future rule could still present a cycle — and a cycle would put a grant in
 * its own „behält danach" list, offering to revoke the very thing it promises to keep. So anything `grant` is
 * itself covered by is excluded, which cannot loop by construction.
 */
export function supersededGrants(grant: RoleGrant, all: readonly RoleGrant[]): RoleGrant[] {
  if (!grantIsActive(grant)) return [];
  const coverage = grantCoverage(grant);
  const covers = new Set(coverage.coversGrantIds);
  if (!covers.size) return [];
  const coversMe = new Set(coverage.coveredByGrantIds);
  return all.filter(other =>
    other.id !== grant.id && grantIsActive(other) && !coversMe.has(other.id) && covers.has(other.id));
}

/**
 * The scope of one grant, named as far as the data allows: the location's name, its id when the name was not
 * resolved, or „Gesamte Firma" for a company-wide grant.
 */
export function grantScopeName(grant: Pick<RoleGrant, 'shardId' | 'shardName'>): string {
  if (!grant.shardId) return 'Gesamte Firma';
  return grant.shardName || grant.shardId;
}

/** One grant on one line — „Standortverwaltung · Hafenstraße" — for the revoke dialog's survivor list. */
export function grantSummaryLabel(grant: Pick<RoleGrant, 'role' | 'shardId' | 'shardName'>): string {
  return `${roleLabel(grant.role)} · ${grantScopeName(grant)}`;
}

/**
 * The sentence under an `[abgedeckt]` badge (#759) — „Durch „Firmeninhaber:in" abgedeckt" — or `null` when
 * the grant is not covered and nothing should be rendered at all.
 *
 * Informational, never a warning: a covered grant takes nothing away, it merely confers nothing new. The copy
 * says what covers it, so the revoke dialog's survivor list is a confirmation of something already on screen
 * rather than a surprise.
 */
export function coverageSentence(grant: RoleGrant, all: readonly RoleGrant[]): string | null {
  // A revoked row is never badged. The backend already answers `covered: false` for one — "this access is
  // redundant" says nothing about access that has ended — so this only makes the roster's own rule visible
  // rather than inherited, and keeps a stale list from badging a row it should not.
  if (!grantIsActive(grant) || !grantCoverage(grant).covered) return null;
  const names = [...new Set(coveringGrants(grant, all).map(other => roleLabel(other.role)))];
  if (!names.length) return 'Durch eine weitergehende Rolle abgedeckt';
  const quoted = names.map(name => `„${name}“`);
  const list = quoted.length === 1
    ? quoted[0]
    : `${quoted.slice(0, -1).join(', ')} und ${quoted[quoted.length - 1]}`;
  return `Durch ${list} abgedeckt`;
}

/**
 * The body `POST /grants/invitations` takes.
 *
 * PascalCase keys, matching `EnterContextRequest` in `_auth/context/context.models.ts`: Newtonsoft binds
 * members case-insensitively, and keeping request bodies in the C# member casing makes them greppable against
 * the record they bind to. Explicit `null` (rather than an omitted key) for the optional fields, so a cleared
 * field is unambiguous.
 */
export interface RoleGrantInviteRequest {
  Scope: GrantScope;
  CompanyId: string;
  ShardId: string | null;
  Roles: string[];
  Label: string | null;
  BoundEmail: string | null;

  /**
   * Whether the server mails the invitation to {@link BoundEmail} instead of the issuer handing the link over
   * (#568). Optional, and false server-side when omitted.
   *
   * Asking for it never costs the code — the response carries it either way and
   * {@link RoleGrantInvitation.emailDelivery} says what actually happened, so a caller that asked for a mail and
   * read anything but {@link InviteEmailDelivery.Sent} must put the link on screen after all.
   */
  DeliverByEmail?: boolean;

  /**
   * Whether {@link BoundEmail} is a **hard** requirement: only a provider-*verified* address equal to it may
   * redeem (#568). Optional, and false server-side when omitted — which is the pre-#568 behaviour exactly.
   *
   * Both this and {@link DeliverByEmail} stand on the address: either without a syntactically valid
   * {@link BoundEmail} answers `E_GRANT_INVITE_EMAIL_REQUIRED` rather than being silently dropped, because an
   * issuer who believes an invitation is locked to one person has no way to see afterwards that it is not.
   */
  EnforceBoundEmail?: boolean;

  /**
   * The browser origin the mailed invitation link must point at.
   *
   * **Leave it null from the SPA.** The controller prefers the request's own `Origin` header — the value the
   * server itself observed — and falls back to this field only for callers that have no header to send (Postman,
   * an e2e script). Filling it in from a browser therefore changes nothing, and is deliberately not attempted:
   * one origin, decided in one place.
   */
  Origin?: string | null;

  /**
   * Where to mail the invitation, when {@link DeliverByEmail} is set and the invitation is **not** bound (#584).
   *
   * It exists because delivery and binding are two different decisions and the form now offers one toggle for
   * the second of them only. With the toggle off there is no {@link BoundEmail} for a mail to be addressed to,
   * and before this field the address had nowhere left to live once the binding was gone.
   *
   * **Never send both with different values.** The server answers
   * `E_GRANT_INVITE_DELIVERY_ADDRESS_CONFLICT` for that, because "sent to A, redeemable only by B" is a message
   * its recipient cannot act on. The form therefore fills in exactly one of the two, decided by the toggle.
   */
  DeliveryEmail?: string | null;
}

/**
 * What `POST /auth/local/redeem/lookup` answers about one address on one invitation (#583). Mirrors
 * `LocalRedeemLookup` in `API.Core/Models/Authentication/RoleGrantModels.cs`.
 *
 * **Why the landing asks at all.** Redeeming with an address that already has a native account is a sign-in, not
 * a sign-up: the password is proved rather than set. A door that cannot tell the two apart has to ask for a
 * password confirmation somebody signing in does not need, and label a button with the opposite of what pressing
 * it will do. Asking first is what lets the second step say one honest thing.
 *
 * **It is an answer about an invitation, not about the directory.** The call is gated on the invitation code, so
 * the only person who can ask is somebody who could learn the same fact by redeeming it. An invalid, expired,
 * revoked or already-redeemed code answers `E_GRANT_INVITE_INVALID` and says nothing about the address.
 */
export interface InvitationRedeemLookup {
  /** Whether a native (e-mail + password) account already exists for the address that was asked about. */
  exists: boolean;
  /**
   * Whether this invitation insists on exactly its bound address, so no other one may be tried.
   *
   * The preview already says as much through `nativeRedeem`, and this is the same fact from the endpoint that
   * will actually decide. The landing uses it to keep the address fixed rather than offering a way back to a
   * field whose every other value is a guaranteed refusal.
   */
  locked: boolean;
}

/**
 * Roles grantable at company scope. **Mirrors `GrantIssuancePolicy.CompanyGrantable`** — the server is the
 * authority and rejects anything outside it with `E_GRANT_INVITE_ROLE_NOT_GRANTABLE`; this list only decides
 * what the form *offers*, so a drift shows up as a refusal rather than as privilege.
 */
export const COMPANY_GRANTABLE_ROLES: readonly string[] =
  ['CompanyOwner', 'Payroll', 'Steuerberater', 'ShiftLead', 'Auditor', 'SensitiveDataAccessor'];

/** Roles grantable at shard scope. Mirrors `GrantIssuancePolicy.ShardGrantable`. */
export const SHARD_GRANTABLE_ROLES: readonly string[] =
  ['ShardAdmin', 'Operator', 'ShiftLead', 'Auditor', 'SensitiveDataAccessor'];

/** What the invite form may offer at `scope`. */
export function grantableRoles(scope: GrantScope): readonly string[] {
  return scope === GrantScope.Shard ? SHARD_GRANTABLE_ROLES : COMPANY_GRANTABLE_ROLES;
}

/** The four states an invitation can be in, in the order they are checked. */
export type InvitationStatus = 'revoked' | 'redeemed' | 'expired' | 'pending';

/**
 * Which state an invitation is in.
 *
 * Order matters: a revoked invitation that was previously redeemed is **revoked** (its grants went with it),
 * and an expired-but-redeemed one is redeemed — expiry only ever bounded the window to redeem. `now` is a
 * parameter so the expiry boundary is testable without freezing the clock.
 */
export function invitationStatus(invitation: RoleGrantInvitation, now: Date = new Date()): InvitationStatus {
  if (invitation.revokedAt) return 'revoked';
  if (invitation.redeemedAt) return 'redeemed';
  if (new Date(invitation.expiration).getTime() <= now.getTime()) return 'expired';
  return 'pending';
}

/** German label for an invitation state. */
export function invitationStatusLabel(status: InvitationStatus): string {
  switch (status) {
    case 'revoked':
      return 'Widerrufen';
    case 'redeemed':
      return 'Eingelöst';
    case 'expired':
      return 'Abgelaufen';
    default:
      return 'Offen';
  }
}

/**
 * Badge classes for an invitation state. `text-*-700` throughout rather than `-600`: at the badge's `text-xs`
 * the lighter shade fails WCAG AA on a `-50` surface (style guide, "Status badge / pill").
 */
export function invitationStatusClass(status: InvitationStatus): string {
  switch (status) {
    case 'revoked':
      return 'bg-red-50 text-red-700';
    case 'redeemed':
      return 'bg-green-50 text-green-700';
    case 'expired':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-amber-50 text-amber-700';
  }
}

/**
 * Whether this invitation's code is unrecoverable — i.e. always, unless we are looking at the very response
 * that created it.
 *
 * Exists as a named predicate rather than an inline `!code` so the screens read as a statement about the
 * product ("the code is gone") instead of a null check that could plausibly be a loading state.
 */
export function invitationCodeIsGone(invitation: RoleGrantInvitation): boolean {
  return !invitation.code;
}

/**
 * What to tell the issuer about the mail they asked for (#568) — one wording, read by the invite-code dialog
 * and by the employee roster's alert banner.
 *
 * `null` for {@link InviteEmailDelivery.NotRequested} and for an absent value, because a screen that never
 * asked for a mail must say nothing about one: an "es wurde keine E-Mail versendet" line on a hand-over
 * invitation reads as a failure of something that was never attempted.
 *
 * The two unhappy outcomes are deliberately ONE sentence shape. `NoAddress` and `Failed` differ in cause but
 * not in what the reader must now do — hand the link over — and the cause is already visible on the form
 * (there is an address, or there is not).
 */
export function inviteDeliveryMessage(delivery: InviteEmailDelivery | undefined,
                                      address: string | undefined | null): string | null {
  switch (delivery) {
    // Short on purpose (#584): on the result dialog this is a receipt, and the dialog's subject is already the
    // invitation that was just created. "Gesendet an X." is the whole of the new information.
    case InviteEmailDelivery.Sent:
      return `Gesendet an ${address || 'die hinterlegte Adresse'}.`;
    case InviteEmailDelivery.NoAddress:
      return 'Es war keine verwendbare E-Mail-Adresse hinterlegt. Es wurde keine E-Mail versendet. Bitte '
        + 'geben Sie den Einladungslink selbst weiter.';
    case InviteEmailDelivery.Failed:
      return 'Die E-Mail konnte nicht versendet werden. Die Einladung ist trotzdem gültig. Bitte geben Sie '
        + 'den Einladungslink selbst weiter.';
    default:
      return null;
  }
}

/** Whether a delivery outcome is one the issuer has to act on (hand the link over themselves). */
export function inviteDeliveryFailed(delivery: InviteEmailDelivery | undefined): boolean {
  return delivery === InviteEmailDelivery.NoAddress || delivery === InviteEmailDelivery.Failed;
}

/**
 * The one sentence describing an invitation's address binding — `null` when there is no binding to describe,
 * which is the ordinary case since #584.
 *
 * ## Why "nothing" is the right sentence for an unbound invitation
 *
 * The form now offers a single toggle, „Nur mit dieser E-Mail-Adresse einlösbar", and it is off by default. An
 * invitation that names no address is simply redeemable by whoever holds the link, and saying so on every row
 * would repeat the default back at the reader on almost every invitation they ever issue. The binding is the
 * exception and reads as one.
 *
 * ## Why the weak binding still has its own wording
 *
 * The screens can no longer issue one, but rows created before #584 carry it and `InvitationEmailGate` still
 * honours it. Unenforced, the gate compares only an address the provider **vouches for** and falls back to the
 * code alone for one it does not (an unverified workspace address, an Apple private relay) — so the binding is a
 * second factor where it can be and nothing where it cannot. Printing "Nur mit … einlösbar" over that would be
 * the strong claim on the weak case, which is the mistake #568 already fixed once.
 */
export function bindingSentence(invitation: Pick<RoleGrantInvitation, 'boundEmail' | 'enforceBoundEmail'>)
  : string | null {
  if (!invitation.boundEmail) return null;
  return invitation.enforceBoundEmail
    ? `Nur mit ${invitation.boundEmail} einlösbar.`
    : `Gebunden an ${invitation.boundEmail}. Ein Konto mit einer anderen, vom Anbieter bestätigten Adresse `
      + 'wird abgewiesen.';
}

/**
 * Where an invitation was sent, for a row that was mailed (#584) — `null` for one the issuer handed over.
 *
 * Separate from {@link bindingSentence} because delivery and binding are now separate facts, and the list has to
 * be able to state either without implying the other. `deliveryEmail` falls back to `boundEmail` for rows
 * written before the field existed, where the two were necessarily the same address.
 */
export function deliverySentence(
  invitation: Pick<RoleGrantInvitation, 'deliveryEmail' | 'boundEmail' | 'deliveredByEmailAt'>): string | null {
  const address = invitation.deliveryEmail?.trim()
    || (invitation.deliveredByEmailAt ? invitation.boundEmail?.trim() : null);
  return address ? `Gesendet an ${address}.` : null;
}

/**
 * Whether an invitation can be **re-sent** by e-mail (#579): there is an address to send it to, nobody has
 * redeemed it, and nobody has withdrawn it.
 *
 * ## Why the two screens ask this ONE question
 *
 * The access list and the employee's Anmeldung card both offer the button, and each of them could have
 * composed the predicate from what it happens to render. They would then have drifted the moment one of them
 * gained a state the other did not, and the drift would show up as a button that is offered into a guaranteed
 * refusal — which is worse than no button, because the person pressing it learns nothing about why.
 *
 * ## Expiry is deliberately NOT disqualifying
 *
 * It is the ordinary reason to press the button. `RoleGrantService.ResendInvitation` refuses a redeemed
 * (`E_GRANT_INVITE_ALREADY_REDEEMED`) and a revoked (`E_GRANT_INVITE_REVOKED`) invitation and an address-less
 * one (`E_GRANT_INVITE_EMAIL_REQUIRED`), and lets an expired one through on purpose: nobody decided anything
 * by letting a window close. So this mirrors exactly those three refusals and nothing else.
 *
 * The address is checked for *presence*, not validity — {@link looksLikeEmail} is a gate on what a FORM offers
 * for a value somebody is still typing. Here the address is already stored, the server accepted it when the
 * invitation was issued, and re-testing it against a looser or stricter pattern than the server's could only
 * ever hide a button that would have worked.
 */
export function invitationCanBeResent(
  invitation: Pick<RoleGrantInvitation, 'boundEmail' | 'deliveryEmail' | 'redeemedAt' | 'revokedAt'>): boolean {
  // Mirrors the server's own `RoleGrantInvitation.DeliveryAddress` (#584): the delivery address first, the
  // binding only as the fallback for rows written before the two were separable. Asking about `boundEmail`
  // alone would hide the button on exactly the invitations the single toggle now produces — sent somewhere,
  // bound to nobody — even though `ResendInvitation` would have accepted them.
  const address = invitation.deliveryEmail?.trim() || invitation.boundEmail?.trim();
  return !!address && !invitation.redeemedAt && !invitation.revokedAt;
}

/**
 * The link a recipient actually needs: the invite landing page, with the code in the query string.
 *
 * `InviteLandingComponent` (route `invite`) reads `?code=` and previews the invitation anonymously, so this is
 * the whole handover — the bare code is only useful to somebody who already knows where to paste it.
 */
export function inviteLink(code: string, origin: string = location.origin): string {
  return `${origin}/invite?code=${encodeURIComponent(code)}`;
}

/**
 * What an invitation or grant targets, spelled out rather than left as an id.
 *
 * An **employee-link** invitation names the employee instead of the tenant breadth. It is company-scoped like
 * any other, but "Gesamte Firma" would be actively misleading on a code that confers no role anywhere — the
 * only thing it does is make one account able to sign in as one member of staff.
 */
export function scopeDescription(target: {
  scope: GrantScope; companyName?: string; companyId: string; shardName?: string; shardId?: string;
  employeeId?: string; employeeName?: string;
}): string {
  const company = target.companyName || target.companyId;
  if (target.employeeId) {
    return `${company} · Mitarbeiter-Anmeldung: ${target.employeeName || target.employeeId}`;
  }
  if (target.scope === GrantScope.Company) return `${company} · Gesamte Firma`;
  return `${company} · ${target.shardName || target.shardId || 'Unbenannter Standort'}`;
}

/** German label for a scope, for the scope selector and the table cells. */
export function scopeLabel(scope: GrantScope): string {
  return scope === GrantScope.Shard ? 'Standort' : 'Gesamte Firma';
}

/** Comma-joined German role labels — the one line the invitation table, its cards and the revoke-confirm
 * dialog all render a role set as. */
export function roleListLabel(roles: string[]): string {
  return roles.map(roleLabel).join(', ');
}

/**
 * `ApiError.code` → German sentence for every refusal the grant surface can actually answer with.
 *
 * ## Why this table exists at all
 *
 * It replaces rendering the server's `ApiError.description`, which this file used to do **on purpose**: the
 * argument was that `GrantIssuancePolicy` refuses more narrowly than the controller's coarse `[Authorize]`
 * list, so its own explanation ("you can only grant access within a company or shard you administer") beats
 * anything the SPA could guess. The observation was right; the conclusion was not. `description` comes from
 * `ErrorCatalog` and is **developer-facing English by design** — it is written for Postman, logs and docs —
 * so preferring it put English sentences on an otherwise fully German screen at the one moment somebody is
 * stuck (frontend style guide, *"Surfacing an API error — branch on `code`, never render `description`"*).
 *
 * The specificity is kept rather than traded away: every code below gets its own sentence saying what
 * happened **and** what to do next, sourced from that code's XML doc in
 * `API.Core/Authorization/AuthErrorCodes.cs` — the catalog description is a one-line summary, the doc comment
 * above the registration is what the sentence needs to say.
 *
 * ## What to do when a new code appears here
 *
 * Add it, with its own sentence, read from its C# doc comment. Do **not** widen {@link grantErrorMessage} back
 * to `description` and do not make the fallback more specific — an unmapped code degrading to the caller's
 * German fallback is the designed behaviour, not the gap.
 *
 * ## Why these four and not the other five `E_GRANT_*` codes
 *
 * The vocabulary splits by who is on the screen. `GrantHttpService` only ever calls the five admin actions on
 * `RoleGrantController` (`POST/GET /grants…`, `DELETE /grants…`) plus the two employee-link actions on
 * `AccountController`, so the codes below are the complete set reachable from an administering session. The
 * redemption codes — `E_GRANT_INVITE_INVALID`, `E_GRANT_REDEEM_NOT_SOCIAL_SESSION`,
 * `E_GRANT_INVITE_EMAIL_MISMATCH`, `E_GRANT_ALREADY_HELD`, `E_GRANT_REDEEM_EMPLOYEE_LINK_FAILED` — belong to
 * `RoleGrantRedeemController`, which only the invitee's landing reaches; they are already mapped in
 * `authentication/signing-in/signing-in.component.ts` and repeating them here would be a second copy free to
 * drift from the first.
 */
const GRANT_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // Issued when `GrantIssuancePolicy.CanIssue` / `CanInviteEmployeeLink` says no — on inviting *and* on both
  // revokes, so the sentence has to cover taking access away as well as handing it out.
  E_GRANT_INVITE_FORBIDDEN_SCOPE:
    'Dieser Bereich liegt außerhalb Ihrer Verwaltung. Zugänge können Sie nur in einer Firma oder an einem ' +
    'Standort vergeben und entziehen, die bzw. den Sie selbst verwalten — Plattform-Zugänge lassen sich ' +
    'überhaupt nicht per Einladung vergeben.',
  // `GrantIssuancePolicy.IsGrantable`: the role itself is not on offer at the chosen scope.
  E_GRANT_INVITE_ROLE_NOT_GRANTABLE:
    'Diese Rolle lässt sich in diesem Bereich nicht per Einladung vergeben. Wählen Sie eine andere Rolle oder ' +
    'einen anderen Bereich.',
  // Both revokes answer this for a row that is already gone — almost always a stale list, so say so.
  E_GRANT_NOT_FOUND:
    'Dieser Eintrag besteht nicht mehr — vermutlich wurde er zwischenzeitlich widerrufen oder eingelöst. ' +
    'Bitte laden Sie die Seite neu.',
  // `GrantIssuancePolicy.OpensNothingWithoutAShard` (#279, Defect A). Checked at ISSUE time for the same reason
  // the employee-link check below is: the invitee cannot create a location, so the only person who can act on
  // this sentence is the one reading it now. It therefore names the fix rather than the rule.
  E_GRANT_INVITE_COMPANY_HAS_NO_SHARD:
    'Diese Firma hat noch keinen Standort. Ein firmenweiter Zugang gilt für alle Standorte und würde deshalb ' +
    'nichts öffnen. Legen Sie zuerst einen Standort an. Laden Sie danach erneut ein.',
  // #568. Reachable from BOTH issue paths, and the sentence has to cover both: the management form was told an
  // address and it was not usable, while the employee path DERIVES one from the employee row — so there it
  // means "this employee has no usable address on file". Naming the two options (add an address, or choose the
  // other delivery) is the whole remedy, and both are on the screen the reader is already looking at.
  //
  // The first quotation is the segment's own label, not a description of it: all three screens that issue an
  // invitation now carry the same „Per E-Mail senden" / „Nur Link erzeugen" choice, and a refusal that names a
  // control by a word nobody can see on the screen is a refusal nobody can act on.
  E_GRANT_INVITE_EMAIL_REQUIRED:
    'Für „Per E-Mail senden“ und „Nur mit dieser E-Mail-Adresse einlösbar“ wird eine gültige ' +
    'E-Mail-Adresse benötigt. Tragen Sie eine Adresse ein, wählen Sie „Nur Link erzeugen“ und schalten Sie ' +
    'die Bindung ab. Beim Mitarbeiter hinterlegen Sie die Adresse in dessen Stammdaten.',
  // #584. Reachable only from the management form, and only if it ever sent both addresses at once. The form
  // fills in exactly one of the two, so this sentence exists for the case the screen cannot predict — and it
  // names the remedy rather than the rule, because the reader has both fields in front of them.
  E_GRANT_INVITE_DELIVERY_ADDRESS_CONFLICT:
    'Die Einladung würde an eine Adresse gehen und nur mit einer anderen einlösbar sein. Verwenden Sie für ' +
    'beides dieselbe Adresse.',
  // Employee-link invitations are checked at ISSUE time precisely so somebody is present to read this.
  E_EMPLOYEE_INVITE_UNKNOWN_EMPLOYEE:
    'Für diesen Mitarbeiter kann keine Einladung ausgestellt werden: er gehört nicht (mehr) zu dieser Firma ' +
    'oder sein Arbeitsverhältnis ist beendet.',
  // ----- re-sending (#579) -----
  //
  // Both are 409s about the invitation's STATE, and both get their own sentence because the remedies differ —
  // which is exactly why `AuthErrorCodes` gives them two codes instead of one. Sourced from those two doc
  // comments: a redeemed invitation means the person is already in, so the next move is to look at their
  // access; a revoked one was withdrawn on purpose, and a button labelled "erneut senden" must not be the way
  // that decision is quietly undone.
  E_GRANT_INVITE_ALREADY_REDEEMED:
    'Diese Einladung wurde bereits eingelöst. Die eingeladene Person hat den Zugang also schon. Sehen Sie ' +
    'sich deren Zugang an, statt einen weiteren Code zu senden.',
  E_GRANT_INVITE_REVOKED:
    'Diese Einladung wurde widerrufen. Das war eine bewusste Entscheidung, und „Erneut senden“ macht sie ' +
    'nicht rückgängig. Stellen Sie eine neue Einladung aus, wenn der Zugang doch gebraucht wird.'
};

/**
 * The German sentence for a failed grant call.
 *
 * @param error    whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a failure this table does not recognise. Per call site rather than
 *                 one global string, because "Die Einladung konnte nicht erstellt werden." and "Die Rolle
 *                 konnte nicht entzogen werden." are the useful halves of an otherwise contentless refusal —
 *                 and because a bare 400 is routine here: every `/grants/*` action answers a missing field
 *                 with `ResponseModel<T>.BadRequest(ResponseErrors.EmptyFieldsProvided)`, whose first
 *                 positional parameter is the *object result*, so `ErrorCode` stays null and no `ApiError` is
 *                 emitted at all.
 */
export function grantErrorMessage(error: unknown, fallback: string): string {
  const code = readErrorCode(error);
  return (code && GRANT_ERROR_MESSAGES[code]) || fallback;
}

/**
 * The one code whose sentence a **re-send** cannot borrow from {@link GRANT_ERROR_MESSAGES}.
 *
 * `E_GRANT_INVITE_EMAIL_REQUIRED` is answered on three different paths, and the mapping above is written for
 * the two ISSUE ones: it names the two switches on the invite form and tells the reader to fill in an address
 * or switch them off. Every word of that is actionable there. On a re-send there is no form, no switch and no
 * address field — the invitation was issued long ago, and what the server is saying is "this one was never
 * bound to an address, so there is nowhere to send it". Reusing the issue sentence would send somebody looking
 * for two toggles that are not on their screen.
 *
 * A second table rather than a widened first one, on the {@link SELF_GRANT_ERROR_MESSAGES} precedent: the RULE
 * is shared and lives once, on the server. What differs is who is reading and what they can do next.
 */
const RESEND_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  E_GRANT_INVITE_EMAIL_REQUIRED:
    'An dieser Einladung hängt keine verwendbare E-Mail-Adresse. Es gibt also keine Adresse, an die wir sie ' +
    'senden könnten. Stellen Sie eine neue Einladung mit E-Mail-Adresse aus.'
};

/**
 * The German sentence for a failed **re-send** (#579).
 *
 * Delegates to {@link grantErrorMessage} for everything the two screens already speak — the scope refusal, the
 * stale-row 404, and the two #579 state refusals — and overrides only where the shared sentence would describe
 * the wrong act. That keeps one vocabulary with one documented exception, rather than a second copy of five
 * sentences free to drift from the first.
 *
 * @param error    whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a refusal neither table recognises.
 */
export function resendErrorMessage(error: unknown, fallback: string): string {
  const code = readErrorCode(error);
  return (code && RESEND_ERROR_MESSAGES[code]) || grantErrorMessage(error, fallback);
}

// ---------------------------------------------------------------------------------------------------------
// Self-activation (#279) — a platform owner granting ITSELF access to a tenant, from the context picker.
// ---------------------------------------------------------------------------------------------------------

/**
 * The body `POST /grants/self` takes.
 *
 * PascalCase keys and a numeric {@link GrantScope}, for the same two reasons the invite request has them: the
 * monolith binds members case-insensitively but keeping the C# casing makes the body greppable against the
 * record it binds to, and Newtonsoft sends enums as ordinals.
 *
 * Deliberately a **separate** shape from {@link RoleGrantInviteRequest} rather than the same one addressed at
 * oneself. An invitation is a bearer capability handed over and redeemed by somebody else; the two halves are
 * what make it auditable as a hand-over. Self-activation has no second party, and reusing the invite shape
 * would leave a redeemed invitation row implying an invitee who never existed.
 */
export interface SelfGrantRequest {
  Scope: GrantScope;
  CompanyId: string;
  ShardId: string | null;
  Roles: string[];
  /**
   * Why. Optional — the server does not refuse a request without one, because a gate that only obstructs the
   * honest operator is not a gate — but the panel asks for it, since "why" is the one thing the audit trail
   * cannot reconstruct afterwards.
   */
  Reason: string | null;
  /**
   * How long the grant stays live, in whole hours — the panel's duration slider, `1`–`8`, default `1`.
   *
   * Self-activation is a privilege the operator hands themself, so unlike an invitation (which lives until
   * somebody revokes it) it is deliberately short-lived by construction: the slider caps it at a working
   * afternoon rather than leaving a standing grant an operator has to remember to clean up.
   */
  DurationHours: number;

  /**
   * Development-only: a sub-hour lifetime in MINUTES, so the expiry sweep, countdown and hard cutoff can be
   * exercised without waiting an hour. The server honours it ONLY when it runs as Development and ignores it in
   * every deployed environment, so it can never shorten a real grant. Sent only by the dev-mode "Debug" button.
   */
  DebugDurationMinutes?: number;
}

/**
 * One of the calling account's own **active grants** — self-activated *and* invitation-redeemed alike — as
 * `GET /grants/mine` reports it. Mirrors `API.Core/Models/Authentication/RoleGrantModels.cs`'s
 * `MyRoleGrantDto`; the source for "Ihre freigeschalteten Zugänge" on the context chooser.
 *
 * **Not scoped to self-grants alone**, whatever the panel's own name suggests: `MyRoleGrantsController` answers
 * for any authenticated principal holding a grant, self-activated or invited — its `[Authorize]` is
 * deliberately the bare "signed in" check, unlike {@link GrantHttpService.revokeGrant}'s admin-gated endpoint.
 * A row from an invitation therefore carries no {@link expiresAt} at all, same as a lapsed-never self-grant
 * would if one existed — the panel must read "no expiry" as "permanent", not as "something went wrong".
 *
 * A response shape, not a request one, so — unlike {@link SelfGrantRequest} — it is camelCase like every other
 * response shape in this file (`RoleGrantInvitation`, `RoleGrant`, `SelfGrantResult`); only request BODIES take
 * the PascalCase/Newtonsoft treatment. Every nullable field is typed `| null` rather than `?:` even though the
 * monolith's `NullValueHandling.Ignore` actually OMITS a null key (so a real response leaves it `undefined`,
 * not present-and-null) — matching `MyRoleGrantDto`'s C# nullability one field at a time reads clearer here
 * than the file's usual omitted-key convention, and every reader below already treats `null`/`undefined`
 * identically (`!grant.expiresAt`, `||` fallbacks), so nothing depends on telling the two apart.
 */
export interface SelfGrantSummary {
  id: string;
  companyId: string;
  /** Null if the company row can no longer be resolved (`MyRoleGrantDto.CompanyName` is `string?`). */
  companyName: string | null;
  shardId: string | null;
  shardName: string | null;
  scope: GrantScope;
  /**
   * The single role this grant confers, as a one-element list — `MyRoleGrantsController` emits ONE row per
   * `RoleGrant`, so an account holding three roles at one shard shows up as three rows here, never one row
   * with three chips. The list shape (rather than a scalar) exists to match the invitation-shaped DTOs the
   * front end already consumes and to leave room for a future grouped view without a wire change — grouping
   * is deliberately NOT done client-side today.
   */
  roleNames: string[];
  /** ISO timestamp, UTC — when the grant was created. */
  grantedAt: string;
  /**
   * ISO timestamp, UTC — when the grant stops being live automatically, or `null` for a permanent one. Always
   * set for a self-grant (`SelfGrantRequest.DurationHours` clamped to 1–8h server-side); always `null` for an
   * invitation-redeemed or legacy-claim grant, which has no such lifetime.
   */
  expiresAt: string | null;
}

/**
 * Where a self-grant summary applies, in the same words {@link SelfActivationPanelComponent}'s own confirmation
 * sentence uses for the tenant it just granted access to — one phrasing for "this is the tenant", read either
 * right after granting it or later from the list of what is still active.
 */
export function selfGrantTargetLabel(grant: {
  scope: GrantScope; companyId: string; companyName?: string | null; shardId?: string | null;
  shardName?: string | null;
}): string {
  const company = grant.companyName || grant.companyId;
  if (grant.scope === GrantScope.Shard) {
    return `${grant.shardName || grant.shardId || 'Unbenannter Standort'} (${company})`;
  }
  return `${company} — gesamte Firma`;
}

/**
 * What a self-activation produced. Carries the resolved tenant **names**, because the panel shows this back as
 * a confirmation somebody reads — an id would make them go and look it up to check they hit the right tenant.
 *
 * `granted` is never empty on a success: a request whose every role was already held answers
 * `E_GRANT_ALREADY_HELD` instead, so "it worked" and "there was nothing to do" cannot be told apart only by
 * counting.
 */
export interface SelfGrantResult {
  scope: GrantScope;
  companyId: string;
  companyName?: string;
  shardId?: string;
  shardName?: string;
  granted: {grantId: string; role: string}[];
  /**
   * ISO timestamp, UTC — when the created grant(s) automatically expire, stamped server-side from the clamped
   * `SelfGrantRequest.DurationHours` (#279 time-gating). Always present for a self-grant; optional here only
   * because an older backend during the rollout window would omit it.
   */
  expiresAt?: string;
}

/**
 * `ApiError.code` → German sentence for the refusals **self-activation** can answer with.
 *
 * ## Why this is a second table and not four more rows in {@link GRANT_ERROR_MESSAGES}
 *
 * Two of these codes are shared with the invite screen — `E_GRANT_INVITE_ROLE_NOT_GRANTABLE` and
 * `E_GRANT_INVITE_FORBIDDEN_SCOPE` — and the sentences there are written for somebody handing access to a
 * colleague: they say *"per Einladung vergeben"* and *"Zugänge können Sie nur … vergeben und entziehen"*.
 * Neither is true of an operator activating themselves, and a sentence that describes the wrong act is worse
 * than a generic one — it sends the reader looking for an invitation form that is not on their screen.
 *
 * The rule the codes express is genuinely shared and lives once, on the server, in `GrantIssuancePolicy`. What
 * differs is who is reading, and that is what the two tables are for.
 */
const SELF_GRANT_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // The service's restatement of the route's `[Authorize(Roles = PlatformOwner)]`. Over HTTP the attribute
  // answers first with a bare 403 carrying no code at all, so most readers land on the caller's fallback —
  // this is here for the day the action becomes reachable from elsewhere.
  E_GRANT_SELF_FORBIDDEN:
    'Nur die Plattformverwaltung darf sich selbst Zugang zu einer Firma erteilen.',
  // `GrantIssuancePolicy.IsGrantable`. The load-bearing case: PlatformOwner, Register and Employee are never
  // grantable, so the sentence has to say that some roles are out of reach here at all, not merely misplaced.
  E_GRANT_INVITE_ROLE_NOT_GRANTABLE:
    'Mindestens eine gewählte Rolle lässt sich in diesem Bereich nicht erteilen. Die Plattformverwaltung, ' +
    'Kassengeräte und Mitarbeiterzugänge sind davon grundsätzlich ausgenommen.',
  // The scope shape was wrong (a location scope with no location, or a company scope naming one).
  E_GRANT_INVITE_FORBIDDEN_SCOPE:
    'Bereich und Standort passen nicht zusammen. Wählen Sie „Gesamte Firma“ ohne Standort oder „Standort“ ' +
    'mit einem Standort.',
  // Not an error the operator caused: they already hold what they asked for, and the next move is to enter it.
  E_GRANT_ALREADY_HELD:
    'Diese Rechte halten Sie hier bereits. Der Bereich steht Ihnen in der Auswahl schon offen.',
  // A password session has no external identity for a grant to hang off.
  E_GRANT_REDEEM_NOT_SOCIAL_SESSION:
    'Für dieses Konto lässt sich kein Zugang hinterlegen. Melden Sie sich über Microsoft, Google oder Apple an.',
  // `GrantIssuancePolicy.OpensNothingWithoutAShard` (#279, Defect A). Its own sentence rather than the invite
  // one for the reason this whole table exists: the reader here IS the platform owner, so "create a location
  // first" is an instruction they can carry out immediately, not a request to make of somebody else.
  E_GRANT_INVITE_COMPANY_HAS_NO_SHARD:
    'Diese Firma hat noch keinen Standort. Ein firmenweiter Zugang würde deshalb nichts öffnen. Legen Sie ' +
    'zuerst einen Standort an. Schalten Sie sich danach erneut frei.'
};

/**
 * The German sentence for a failed self-activation.
 *
 * @param error    whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a refusal this table does not recognise — an unknown company or
 *                 shard answers a bare 404 with no `ApiError` body, so the fallback is a real code path here
 *                 rather than a formality.
 */
export function selfGrantErrorMessage(error: unknown, fallback: string): string {
  const code = readErrorCode(error);
  return (code && SELF_GRANT_ERROR_MESSAGES[code]) || fallback;
}

// ---------------------------------------------------------------------------------------------------------
// Direct assignment (#749)
// ---------------------------------------------------------------------------------------------------------

/**
 * The body `POST /grants/direct` takes — the third way access is created, beside an invitation and a
 * self-activation (#749).
 *
 * PascalCase keys and a numeric {@link GrantScope}, for the same two reasons {@link SelfGrantRequest} and
 * {@link RoleGrantInviteRequest} have them: the monolith binds members case-insensitively but keeping the C#
 * casing makes the body greppable against the record it binds to, and Newtonsoft sends enums as ordinals. A
 * string scope here would compile, render and POST a value the server reads as `Company`, silently widening a
 * location grant to the whole tenant.
 *
 * ### Why this is a third shape rather than a mode of one of the other two
 *
 * An **invitation** is a bearer capability handed over and redeemed by a second party; the two halves are what
 * make the hand-over auditable. A **self-activation** has no second party at all. This has a second party who
 * is never asked — so it carries the one field neither of the others needs to require: {@link Reason}.
 */
export interface DirectGrantRequest {
  /** The `ExternalIdentity` that gains the roles. Unknown ⇒ `E_GRANT_DIRECT_IDENTITY_NOT_FOUND`. */
  IdentityId: string;
  Scope: GrantScope;
  CompanyId: string;
  ShardId: string | null;
  Roles: string[];
  /**
   * Why. **Required**, unlike {@link SelfGrantRequest.Reason} — the server answers
   * `E_GRANT_DIRECT_REASON_REQUIRED` for an empty one, and the form keeps its submit button disabled until it
   * is filled.
   *
   * The asymmetry is deliberate and is the whole argument for the field: a self-grant's actor and subject are
   * the same person, so the audit entry already explains itself. A direct grant's does not — the entry would
   * say that one operator handed a third party access, and nothing about why.
   */
  Reason: string;
  /**
   * How long the grant stays live, in whole hours — or **omitted** for a permanent grant, which is the default
   * this form offers.
   *
   * Permanent is the default because this path replaces an invitation, and a redeemed invitation has no
   * expiry: making the replacement time-boxed by construction would quietly change what "give this person
   * access" means. The time box stays available for short loans and is clamped server-side.
   */
  DurationHours?: number;
}

/**
 * What a direct assignment produced. A **superset** of {@link SelfGrantResult}, not a mirror of it: it also
 * names the target and reports whether the write switched a deactivated account back on, neither of which a
 * self-grant has anything to say about.
 *
 * It carries the resolved tenant **names** for the reason the self-grant result does — the dialog shows this
 * back as a confirmation somebody reads, and an id would make them go and look it up to check they hit the
 * right tenant.
 *
 * camelCase, like every other response shape in this file; only request BODIES take the PascalCase treatment.
 * And the monolith sets `NullValueHandling.Ignore`, so every nullable field is **omitted** rather than sent as
 * null — hence the `?` on each, and hence {@link expiresAt} below.
 *
 * `granted` is never empty on a success: a request whose every role was already held answers
 * `E_GRANT_ALREADY_HELD` instead, so "it worked" and "there was nothing to do" cannot be told apart only by
 * counting.
 */
export interface DirectGrantResult {
  /** The identity that now holds the grants, echoed back so the panel can name it without trusting its form. */
  identityId: string;
  /**
   * The target's display name (or e-mail), resolved server-side at write time. Omitted when the identity row
   * carries neither, in which case the screen falls back to what its own picker showed, then to the id.
   */
  identityName?: string;
  scope: GrantScope;
  companyId: string;
  companyName?: string;
  shardId?: string;
  shardName?: string;
  /**
   * One entry per grant actually created. `grantId`, not `id` — the wire field comes from
   * `RoleGrantRedeemedRole(string GrantId, string Role)`, the same record the redemption result uses.
   */
  granted: {grantId: string; role: string}[];
  /**
   * ISO timestamp, UTC — when the created grants automatically expire.
   *
   * **Absent means permanent, and that is this path's ordinary case** (unlike
   * {@link SelfGrantResult.expiresAt}, which is always set). The server sends `DateTime?` under
   * `NullValueHandling.Ignore`, so a permanent grant omits the key entirely — read it as falsy
   * (`!result.expiresAt`), never as `=== null`, which is false for an omitted key and would print an expiry
   * sentence with `undefined` in it.
   */
  expiresAt?: string;
  /**
   * Whether this assignment **switched a deactivated account back on**. It had been deactivated because it
   * held nothing at all, and granting it something restored its login.
   *
   * Reported rather than silent because it changes what the operator just did: they did not only hand over a
   * role, they brought back an access somebody had previously removed. The confirmation says so.
   */
  reactivated: boolean;
}

/**
 * `ApiError.code` → German sentence for the refusals **direct assignment** can answer with.
 *
 * ## Why a third table and not more rows in the other two
 *
 * Four of these codes are shared with the invite and self-activation screens
 * (`E_GRANT_INVITE_ROLE_NOT_GRANTABLE`, `E_GRANT_INVITE_FORBIDDEN_SCOPE`, `E_GRANT_INVITE_COMPANY_HAS_NO_SHARD`
 * and `E_GRANT_ALREADY_HELD`) and neither existing sentence is true here. The invite table's read „per
 * Einladung vergeben" — there is no invitation on this path. The self table's read „Diese Rechte halten Sie
 * hier bereits" — the holder is somebody else entirely, so that sentence sends the operator looking at their
 * own access instead of the target's.
 *
 * The rule the codes express is genuinely shared and lives once, on the server, in `GrantIssuancePolicy`. What
 * differs is who is reading and about whom, and that is what the three tables are for — the same argument
 * {@link SELF_GRANT_ERROR_MESSAGES} makes, applied once more.
 *
 * Every sentence is written from that code's XML doc in `API.Core/Authorization/AuthErrorCodes.cs`, never from
 * the catalog's developer-facing English `description` (style guide, *"Surfacing an API error — branch on
 * `code`, never render `description`"*).
 */
const DIRECT_GRANT_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // The service's restatement of the route's `[Authorize(Roles = PlatformOwner)]`. Over HTTP the attribute
  // answers first with a bare 403 carrying no code at all, so most readers land on the caller's fallback —
  // this is here for the day the action becomes reachable from elsewhere.
  E_GRANT_DIRECT_FORBIDDEN:
    'Nur die Plattformverwaltung darf einer Person Rechte direkt zuweisen.',
  // The one refusal the form is built to prevent, so it should be unreachable — but the server is the
  // authority on an empty-after-trim reason, not the button's disabled state.
  E_GRANT_DIRECT_REASON_REQUIRED:
    'Bitte geben Sie einen Grund an. Ohne Grund lässt sich diese Zuweisung nicht erteilen, weil sie sonst im '
    + 'Prüfprotokoll nicht nachvollziehbar wäre.',
  // The chosen identity is gone — deleted, or the list was loaded before it was.
  E_GRANT_DIRECT_IDENTITY_NOT_FOUND:
    'Dieses Konto gibt es nicht mehr. Laden Sie die Liste neu und wählen Sie die Person erneut aus.',
  // `GrantIssuancePolicy.IsGrantable`. The load-bearing case: PlatformOwner, Register and Employee are never
  // grantable, so the sentence has to say that some roles are out of reach here at all, not merely misplaced.
  E_GRANT_INVITE_ROLE_NOT_GRANTABLE:
    'Mindestens eine gewählte Rolle lässt sich in diesem Bereich nicht zuweisen. Die Plattformverwaltung, '
    + 'Kassengeräte und Mitarbeiterzugänge sind davon grundsätzlich ausgenommen.',
  // The scope shape was wrong (a location scope with no location, or a company scope naming one).
  E_GRANT_INVITE_FORBIDDEN_SCOPE:
    'Bereich und Standort passen nicht zusammen. Wählen Sie „Gesamte Firma“ ohne Standort oder „Standort“ '
    + 'mit einem Standort.',
  // Not an error the operator caused: the target already holds what was asked for. Named as the TARGET's
  // access, not the reader's — this is the sentence the self-activation table cannot lend.
  E_GRANT_ALREADY_HELD:
    'Diese Person hält die gewählten Rechte hier bereits. Es gibt nichts zuzuweisen.',
  // `GrantIssuancePolicy.OpensNothingWithoutAShard`. The reader IS the platform owner, so "create a location
  // first" is an instruction they can carry out immediately.
  E_GRANT_INVITE_COMPANY_HAS_NO_SHARD:
    'Diese Firma hat noch keinen Standort. Ein firmenweiter Zugang würde deshalb nichts öffnen. Legen Sie '
    + 'zuerst einen Standort an und weisen Sie die Rechte danach erneut zu.'
};

/**
 * The German sentence for a failed direct assignment.
 *
 * @param error    whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a refusal this table does not recognise — an unknown company or
 *                 shard answers a bare 404 with no `ApiError` body, so the fallback is a real code path here
 *                 rather than a formality.
 */
export function directGrantErrorMessage(error: unknown, fallback: string): string {
  const code = readErrorCode(error);
  return (code && DIRECT_GRANT_ERROR_MESSAGES[code]) || fallback;
}

/**
 * Where a direct assignment applies, in the dialog's own words — the {@link selfGrantTargetLabel} sibling for
 * a result whose subject is somebody else.
 *
 * One function rather than a string built in the template, because the confirmation sentence and the summary
 * the operator reads *before* pressing the button have to name the same place in the same way; two copies of
 * that phrasing is how one of them ends up saying „gesamte Firma" for a location grant.
 */
export function directGrantTargetLabel(target: {
  scope: GrantScope; companyId: string; companyName?: string | null; shardId?: string | null;
  shardName?: string | null;
}): string {
  const company = target.companyName || target.companyId;
  if (target.scope === GrantScope.Shard) {
    return `${target.shardName || target.shardId || 'Unbenannter Standort'} (${company})`;
  }
  return `${company} — gesamte Firma`;
}
